
(() => {
  'use strict';

  // TecHub - PushOwl Arabic Localization
  // English remains managed by PushOwl dashboard.

  const isArabic =
    document.documentElement.lang.toLowerCase().startsWith('ar') ||
    /^\/ar(?:\/|$)/i.test(window.location.pathname);

  if (!isArabic) return;

  const COPY = {
    before: {
      en: 'Get Updated with Latest Offers and Products.',
      ar: 'اشترك لتصلك أحدث منتجات TecHub والعروض الحصرية وكل جديد أولًا بأول.'
    },
    after: {
      en: 'Thanks for subscribing. We will keep you posted.',
      ar: 'تم اشتراكك بنجاح! ترقّب أحدث العروض والأخبار من TecHub.'
    },
    button: {
      en: 'Subscribe',
      ar: 'اشترك الآن'
    }
  };

  const normalize = (value) =>
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim();

  const TEXT_SELECTOR =
    'p, span, div, label, strong, em, small, section, article';

  const CONTROL_SELECTOR =
    'button, [role="button"], a';

  const knownFlyoutRoots = new Set();
  const observedRoots = new WeakSet();
  const observedFrames = new WeakSet();

  function elementsIn(scope, selector) {
    if (!scope || !scope.querySelectorAll) return [];

    const result = [];

    if (scope.nodeType === 1 && scope.matches(selector)) {
      result.push(scope);
    }

    result.push(...scope.querySelectorAll(selector));

    return result;
  }

  function findMessage(scope, message) {
    const candidates = elementsIn(scope, TEXT_SELECTOR)
      .filter((element) =>
        normalize(element.textContent) === normalize(message)
      )
      .filter((element) =>
        !element.querySelector(
          'button, input, textarea, select, svg, img'
        )
      );

    if (!candidates.length) return null;

    // Prefer the smallest matching text element.
    candidates.sort((a, b) => {
      const depth = (element) => {
        let count = 0;
        while (element) {
          count++;
          element = element.parentElement;
        }
        return count;
      };

      return depth(b) - depth(a);
    });

    return candidates[0];
  }

  function setArabicDirection(element, isButton = false) {
    if (!element) return;

    element.setAttribute('dir', 'rtl');
    element.style.direction = 'rtl';
    element.style.textAlign = isButton ? 'center' : 'right';
  }

  function translateMessage(element, translation) {
    if (!element) return;

    element.textContent = translation;
    setArabicDirection(element);
  }

  function findFlyoutRoot(messageElement) {
    let current = messageElement;

    for (let i = 0; current && i < 12; i++) {
      const identifier = [
        current.id || '',
        typeof current.className === 'string'
          ? current.className
          : ''
      ].join(' ');

      // Prefer a recognizable PushOwl container.
      if (/pushowl|powl/i.test(identifier)) {
        return current;
      }

      // Otherwise find the closest ancestor containing
      // the original Flyout subscribe button.
      const controls = elementsIn(current, CONTROL_SELECTOR);

      const hasSubscribeButton = controls.some(
        (element) =>
          normalize(element.textContent) === COPY.button.en
      );

      if (hasSubscribeButton) return current;

      current = current.parentElement;
    }

    return null;
  }

  function translateButton(root) {
    if (!root) return;

    const buttons = elementsIn(root, CONTROL_SELECTOR);

    const button = buttons.find(
      (element) =>
        normalize(element.textContent) === COPY.button.en
    );

    if (!button) return;

    // Preserve nested icons and the original click handler.
    const walker = button.ownerDocument.createTreeWalker(
      button,
      NodeFilter.SHOW_TEXT
    );

    let textNode;

    while ((textNode = walker.nextNode())) {
      if (normalize(textNode.nodeValue) === COPY.button.en) {
        const original = textNode.nodeValue;
        textNode.nodeValue = original.replace(
          original.trim(),
          COPY.button.ar
        );
      }
    }

    if (
      normalize(button.getAttribute('aria-label')) === COPY.button.en
    ) {
      button.setAttribute('aria-label', COPY.button.ar);
    }

    setArabicDirection(button, true);
  }

  function scanScope(scope) {
    if (!scope) return;

    // Find and translate the pre-subscription message.
    const before = findMessage(scope, COPY.before.en);

    if (before) {
      const flyoutRoot = findFlyoutRoot(before);

      if (flyoutRoot) {
        knownFlyoutRoots.add(flyoutRoot);
        translateButton(flyoutRoot);
      }

      translateMessage(before, COPY.before.ar);
    }

    // Translate the success message when it appears.
    const after = findMessage(scope, COPY.after.en);

    if (after) {
      translateMessage(after, COPY.after.ar);
    }

    // Handle buttons injected after the message.
    knownFlyoutRoots.forEach(translateButton);
  }

  function observeRoot(root) {
    if (!root || observedRoots.has(root)) return;

    observedRoots.add(root);

    scanScope(root);
    discoverNestedRoots(root);

    const observer = new MutationObserver((records) => {
      records.forEach((record) => {
        if (record.type === 'characterData') {
          scanScope(record.target.parentElement);
        }

        record.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE ||
              node.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
            scanScope(node);
            discoverNestedRoots(node);
          }
        });
      });
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  function discoverNestedRoots(scope) {
    const elements = elementsIn(scope, '*');

    elements.forEach((element) => {
      // Support accessible/open Shadow DOM.
      if (element.shadowRoot) {
        observeRoot(element.shadowRoot);
      }

      // Support same-origin iframes.
      if (
        element.tagName === 'IFRAME' &&
        !observedFrames.has(element)
      ) {
        observedFrames.add(element);

        const inspectFrame = () => {
          try {
            if (element.contentDocument) {
              observeRoot(element.contentDocument);
            }
          } catch (_) {
            // Cross-origin frames cannot be accessed.
          }
        };

        element.addEventListener('load', inspectFrame);
        inspectFrame();
      }
    });
  }

  // Start monitoring the storefront and PushOwl injections.
  observeRoot(document);

})();
